'use client';

import type { ClassValue } from 'clsx';
import type { ReactNode } from 'react';
import {
  FilterChip,
  FilterChipCopy,
  FilterChipLabel,
  FilterChipRemove,
  FilterChipValue,
} from '@vs-shop/commerce-ui/filterChips';
import { clsxm } from '@vs-shop/utils';
import { defineMessage, useIntl } from 'react-intl';

/** The accessible name of a chip's remove control: the facet, then the value it drops. */
const removeSelectedFilterMessage = defineMessage({
  id: 'component.client.products.productsSelectedFilter.remove',
  defaultMessage: 'Odstranit filtr {label} {value}',
});

/** The same name for a chip whose value is not a string — a range shows money components. */
const removeSelectedFacetMessage = defineMessage({
  id: 'component.client.products.productsSelectedFilter.removeFacet',
  defaultMessage: 'Odstranit filtr {label}',
});

/** Which dimension the chip stands for. The e2e page objects select chips by it. */
export type SelectedFilterType = 'taxon' | 'brand' | 'checkbox' | 'range' | 'text' | 'warehouse';

type Props = {
  filterType: SelectedFilterType;
  /** What the facet is called. */
  label: string;
  /** The chosen value as the chip shows it — a money component for the price range. */
  value: ReactNode;
  /**
   * The chosen value as the remove control's name says it. Absent where the
   * value has no string form — the range chip shows money components — and the
   * name is then the facet alone.
   */
  spokenValue?: string;
  onRemove: () => void;
  className?: ClassValue;
};

/**
 * rcprofi's arrangement of one applied-filter chip: the shared parts, the
 * slots the route's page objects pin, and the remove control named through
 * this tenant's message. The six dimensions map their data onto it.
 */
export default function ProductsSelectedFilterChip({
  filterType,
  label,
  value,
  spokenValue,
  onRemove,
  className,
}: Props) {
  const intl = useIntl();

  return (
    <FilterChip
      data-slot="products-filter-chip"
      data-filter-type={filterType}
      className={clsxm(className)}
    >
      <FilterChipCopy>
        <FilterChipLabel>{label}</FilterChipLabel>
        <FilterChipValue>{value}</FilterChipValue>
      </FilterChipCopy>
      <FilterChipRemove
        onClick={onRemove}
        data-slot="products-filter-chip-remove"
        aria-label={
          typeof spokenValue === 'string'
            ? intl.formatMessage(removeSelectedFilterMessage, { label, value: spokenValue })
            : intl.formatMessage(removeSelectedFacetMessage, { label })
        }
      />
    </FilterChip>
  );
}
